import React, { useCallback } from 'react';
import { Redirect, Route, Switch, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import styled from 'styled-components/macro';
import {
  Tab as TabType,
  useTabs,
  validateCountry,
  validatePageNumber,
  validateRating,
  validateSingleLetter,
  validateSorting,
} from 'src/client/helpers';
import {
  getModelSortTypes,
  getPhotographerSortTypes,
  modelsAndArtistsRoutes as allRoutes,
  routes,
} from '../../../utils/router';
import { Models } from '../index';
import { Photographers } from '../../Photographers';
import CountTabs from '../../CountTabs/CountTabs';
import CountTab from '../../CountTabs/CountTab';
import { useSelector } from 'src/client/redux';
import { ConstrainedContainer, WidePageWrapper } from 'src/client/constants/mediaQueries';
import { IconOnlySortAndViewDropdown } from 'src/client/components/dropdowns';
import { ContentHeader } from 'src/client/components/ui/ContentHeader';
import { SORT_TYPES as SORT_TYPES_MODELS } from 'src/shared/constants/models';
import { SORT_TYPES as SORT_TYPES_PHOTOGRAPHERS } from 'src/shared/constants/photographers';
import { useWidePageContext } from '../../App/WidePageContextProvider';
import { OnPageText } from 'src/client/components/OnPageSeo/OnPageText/OnPageText';
import { MainPageTitle } from 'src/client/components/layouts/MainPageTitle';
import { ModelsAIChatDemo } from '../Models/ModelsAIChatDemo';
import { useFeatureFlags } from 'src/client/helpers/useFeatureFlags';

const {
  modelsCountry: modelsCountryRoute,
  modelsCountryAlphabetical: modelsCountryAlphabeticalRoute,
  models: modelsRoute,
  modelsAlphabetical: modelsAlphabeticalRoute,
  modelsUserRated: modelsUserRatedRoute,
  photographers: photographersRoute,
  modelsAI: modelsAIRoute,
} = routes.modelsAndArtists.routes;

const modelsAndArtistTabs: Array<TabType> = [
  {
    value: 'Models',
    to: '/models',
    name: 'Models',
    headerTitle: 'Erotic Models',
  },
  {
    value: 'Photographers',
    to: '/photographers',
    name: 'Artists',
    headerTitle: 'Photographers',
  },
  {
    value: 'AI Chat Demo',
    to: '/ai-chat',
    name: '🔥 AI Chat Demo',
    headerTitle: '🔥 AI Chat Demo',
  },
];

const sortKeyByTab = {
  Models: 'modelsSorting',
  Photographers: 'modelsSorting',
};

const sortTypes = {
  Models: SORT_TYPES_MODELS,
  Photographers: SORT_TYPES_PHOTOGRAPHERS,
  'AI Chat Demo': [],
};

export const ModelsAndArtists = () => {
  const { config } = useSelector((state) => state.app);
  const { user } = useSelector((state) => state.auth);
  const modelSortTypes = getModelSortTypes(config);
  const photographerSortTypes = getPhotographerSortTypes(config);
  const { showAIGirlsLink } = useFeatureFlags();
  const showAiLink = showAIGirlsLink && user?.validSubscription;

  const params = useParams<{ country: string }>();
  const { country } = params;

  const widePage = useWidePageContext();

  const translatedModelsTabs: TabType[] | undefined = config?.modelsTabs?.map((tab) => ({
    value: tab.label,
    name: tab.label,
    to: tab.link,
    headerTitle: tab.label,
  }));

  const filteredTabs = modelsAndArtistTabs.filter((tab) => {
    switch (tab.value) {
      case 'Photographers':
        return config.hasPhotographers;
      case 'AI Chat Demo':
        return showAiLink;
      default:
        return true;
    }
  });

  const allTabs = translatedModelsTabs || filteredTabs;
  const [tabs, activeTab] = useTabs('models-and-artists', allTabs, allRoutes);

  const renderContentHeader = useCallback(
    ({ collapsePadding = false, showViewOptionsInTabs = false } = {}) => {
      if (!activeTab) {
        return null;
      }

      const tabComponents = tabs.map((tab) => (
        <CountTab key={tab.value} as={Link} active={activeTab.value === tab.value} {...tab} />
      ));

      const sortOptions = sortTypes[activeTab.value].filter((option) => {
        if (option.requiresAuth) {
          return !!user;
        }
        return true;
      });

      if (showViewOptionsInTabs) {
        tabComponents.push(
          <IconOnlySortAndViewDropdown
            key="options"
            sortSettingsKey={sortKeyByTab[activeTab.value]}
            sortTypes={sortOptions}
            pageType="models"
            tab={activeTab.value}
            country={country}
          />,
        );
      }

      const tabsNode = <StyledTabs countParams={[...tabs.map((tab) => tab.value)]}>{tabComponents}</StyledTabs>;

      return (
        <StyledContentHeader
          collapsePadding={collapsePadding}
          tabs={tabsNode}
          title={null}
          subTitle={<OnPageText contentName="text1" />}
        />
      );
    },
    [activeTab, tabs, country, user],
  );

  return (
    <ModelsAndArtistsContainerSwitcher>
      <MainPageTitle>{activeTab?.headerTitle}</MainPageTitle>
      {renderContentHeader({ collapsePadding: widePage, showViewOptionsInTabs: widePage })}
      <Switch>
        <Route
          exact
          path={modelsCountryAlphabeticalRoute.path}
          render={({ match }) => {
            if (!validateCountry(match.params.country)) {
              return <Redirect to="/models" />;
            }
            if (validateSingleLetter(match.params.firstNameLetter) && validatePageNumber(match.params.page)) {
              return <Models sortId="all" />;
            }
            return <Redirect to={`/models/country/${match.params.country}`} />;
          }}
        />
        <Route
          exact
          path={modelsCountryRoute.path}
          render={({ match }) => {
            if (!validateCountry(match.params.country)) {
              return <Redirect to="/models" />;
            }
            if (!match.params.sortBy) {
              return <Models />;
            }

            if (
              match.params.sortBy &&
              validateSorting(match.params.sortBy, modelSortTypes) &&
              validatePageNumber(match.params.page)
            ) {
              return <Models />;
            }
            return <Redirect to={`/models/country/${match.params.country}`} />;
          }}
        />
        <Route
          exact
          path={modelsAlphabeticalRoute.path}
          render={({ match }) => {
            if (validateSingleLetter(match.params.firstNameLetter) && validatePageNumber(match.params.page)) {
              return <Models sortId="all" />;
            }

            return <Redirect to="/models" />;
          }}
        />
        <Route
          exact
          path={modelsUserRatedRoute.path}
          render={({ match }) => {
            if (validatePageNumber(match.params.page) && validateRating(match.params.rating)) {
              return <Models sortId="my-top" />;
            }

            return <Redirect to="/models" />;
          }}
        />
        <Route
          exact
          path={modelsRoute.path}
          render={({ match }) => {
            if (!match.params.sortBy) {
              return <Models />;
            }
            if (
              match.params.sortBy &&
              validateSorting(match.params.sortBy, modelSortTypes) &&
              validatePageNumber(match.params.page)
            ) {
              const sortType = modelSortTypes.find((sort) => sort.id === match.params.sortBy);

              if (sortType?.requiresAuth && !user) {
                return <Redirect to="/models" />;
              }
              return <Models />;
            }
            return <Redirect to="/models" />;
          }}
        />
        {config.hasPhotographers && (
          <Route
            exact
            path={photographersRoute.path}
            render={({ match }) => {
              if (!match.params.sortBy) {
                return <Photographers />;
              }
              if (
                match.params.sortBy &&
                validateSorting(match.params.sortBy, photographerSortTypes) &&
                (match.params.sortBy === 'all'
                  ? validateSingleLetter(match.params.page)
                  : validatePageNumber(match.params.page))
              ) {
                return <Photographers />;
              }
              return <Redirect to="/photographers" />;
            }}
          />
        )}
        <Route
          exact
          path={modelsAIRoute.path}
          render={() => {
            if (showAiLink) {
              return <ModelsAIChatDemo />;
            }
            return <Redirect to="/models" />;
          }}
        />
      </Switch>
    </ModelsAndArtistsContainerSwitcher>
  );
};

// @TODO
// Remove after all pages are converted to wide mode.
const ModelsAndArtistsContainerSwitcher: React.FC = ({ children }) => {
  const widePage = useWidePageContext();
  if (widePage) {
    return (
      <WidePageWrapper>
        <Div as={ConstrainedContainer} extended>
          {children}
        </Div>
      </WidePageWrapper>
    );
  }

  return <Div className="content container">{children}</Div>;
};

const Div = styled.div<{ extended?: boolean }>`
  .options {
    display: flex;
    align-items: center;
    justify-content: flex-end;

    .sort-dropdown {
      margin-right: 20px;
    }
  }
`;

const StyledContentHeader = styled(ContentHeader)<{ collapsePadding?: boolean }>`
  padding-top: ${(props) => (props.collapsePadding ? '0' : null)};
`;

const StyledTabs = styled(CountTabs)`
  display: flex;
  padding-right: 5px;
  align-items: center;

  .tab {
    padding: 10px 15px;
  }
`;
